import { Link, type PageProps } from "gatsby";
import classNames from "classnames";
import React, { useMemo, useState } from "react";

export type NavItem = {
  title: string;
  to: string;
  children?: NavItem[];
};

type NavItemClickEvent = (
  event: React.MouseEvent<HTMLAnchorElement>,
  item: NavItem,
) => void;

type NavItemWithState = Omit<NavItem, "children"> & {
  isCurrent: boolean;
  isAncestor: boolean;
  hasChildren: boolean;
  children?: NavItemWithState[];
};

const augmentNavItem = (
  item: NavItem,
  location: PageProps["location"],
): NavItemWithState => {
  const isCurrent = location.pathname === item.to;
  const isAncestor = location.pathname.startsWith(item.to) && item.to !== "/";
  const hasChildren = !!(item.children && item.children.length > 0);
  return {
    ...item,
    isCurrent,
    isAncestor,
    hasChildren,
    children: item.children?.map((child) => augmentNavItem(child, location)),
  };
};

export function Nav({
  items,
  location,
  onClickNavItem = () => {},
  className,
  listClassName,
  listItemClassName,
}: {
  items: NavItem[];
  location: PageProps["location"];
  onClickNavItem?: NavItemClickEvent;
  className?: string;
  listClassName?: string;
  listItemClassName?: string;
}) {
  const itemsWithState = useMemo(
    () => items.map((item) => augmentNavItem(item, location)),
    [items, location],
  );
  return (
    <nav className={className}>
      <ol className={listClassName}>
        {itemsWithState.map((item, index) => (
          <NavItem
            key={index}
            item={item}
            onClickNavItem={onClickNavItem}
            className={listItemClassName}
          />
        ))}
      </ol>
    </nav>
  );
}

function NavItem({
  item,
  onClickNavItem = () => {},
  className,
}: {
  item: NavItemWithState;
  onClickNavItem?: NavItemClickEvent;
  className?: string;
}) {
  const [submenuOpen, setSubmenuOpen] = useState(false);
  return (
    <li
      onMouseEnter={() => setSubmenuOpen(true)}
      onMouseLeave={() => setSubmenuOpen(false)}
      className={className}
    >
      <div
        className={classNames("flex", {
          "border-transparent": !submenuOpen,
        })}
      >
        <NavItemLink
          item={item}
          onClick={(event) => onClickNavItem(event, item)}
        />
        {item.children && (
          <button
            onClick={() => setSubmenuOpen(!submenuOpen)}
            className={"border border-white aspect-square block p-2"}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={classNames("fill-current", {
                "transform rotate-180": submenuOpen,
              })}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.29289 8.29289C6.68342 7.90237 7.31658 7.90237 7.70711 8.29289L12 12.5858L16.2929 8.29289C16.6834 7.90237 17.3166 7.90237 17.7071 8.29289C18.0976 8.68342 18.0976 9.31658 17.7071 9.70711L12.7071 14.7071C12.3166 15.0976 11.6834 15.0976 11.2929 14.7071L6.29289 9.70711C5.90237 9.31658 5.90237 8.68342 6.29289 8.29289Z"
              />
            </svg>
          </button>
        )}
      </div>
      {item.children && (
        <div className={"pl-4 2xl:pl-0 2xl:absolute 2xl:top-full"}>
          <div
            className={classNames(
              "grid transition-[grid-template-rows,opacity]",
              {
                "grid-rows-[0fr] opacity-0": !submenuOpen,
                "grid-rows-[1fr]": submenuOpen,
              },
              "2xl:bg-white",
            )}
          >
            <ol className={"overflow-hidden"}>
              {item.children.map((subItem, index) => (
                <NavItem
                  key={index}
                  item={subItem}
                  onClickNavItem={onClickNavItem}
                />
              ))}
            </ol>
          </div>
        </div>
      )}
    </li>
  );
}

function NavItemLink({
  item,
  onClick,
}: {
  item: NavItemWithState;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <Link
      to={item.to}
      className={classNames(
        "py-2 px-4 flex items-center flex-grow hover:underline",
        {
          "bg-white text-primary": item.isCurrent || item.isAncestor,
        },
      )}
      onClick={onClick}
    >
      {item.title}
    </Link>
  );
}
